import { create } from 'zustand';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';

import { firebaseAuth } from '../config/firebase';
import { authApi } from '../api/auth';
import { User } from '../types';

interface AuthState {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  // Internal — called by the auth state observer.
  _setFirebaseUser: (fu: FirebaseUser | null) => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  firebaseUser: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,

  login: async (email, password) => {
    set({ error: null });
    const user = await authApi.login({ email, password });
    set({ user, isAuthenticated: true });
  },

  register: async (email, password, displayName) => {
    set({ error: null });
    const user = await authApi.register({ email, password, displayName });
    set({ user, isAuthenticated: true });
  },

  loginWithGoogle: async () => {
    set({ error: null });
    const user = await authApi.loginWithGoogle();
    set({ user, isAuthenticated: true });
  },

  logout: async () => {
    await authApi.logout();
    set({ user: null, firebaseUser: null, isAuthenticated: false });
  },

  resetPassword: async (email) => {
    await authApi.resetPassword(email);
  },

  _setFirebaseUser: async (fu) => {
    set({ firebaseUser: fu });
    if (!fu) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    // If we already have the user profile in state (set by login/register),
    // don't re-fetch. Otherwise hit /api/auth/me.
    const currentUser = get().user;
    if (currentUser && currentUser.id === fu.uid) {
      set({ isAuthenticated: true, isLoading: false });
      return;
    }

    try {
      const user = await authApi.getMe();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      // Profile not synced yet — happens on the first page load after a
      // Google-popup signup, or if the DB was reset. Trigger a sync.
      try {
        const user = await authApi.sync();
        set({ user, isAuthenticated: true, isLoading: false });
      } catch (err) {
        console.error('Failed to sync user profile:', err);
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    }
  },
}));

// Subscribe once to Firebase's auth state. Every login/logout anywhere in the
// app will flow through this observer, keeping the store in sync.
onAuthStateChanged(firebaseAuth, (fu) => {
  void useAuthStore.getState()._setFirebaseUser(fu);
});
